import { Injectable } from '@angular/core';
import { LayoutService } from '@/theme/service/layout.service';

export type ThemeMode = 'light' | 'dark' | 'system';
type SupportedThemeMode = 'light' | 'dark';

/**
 * @class ThemeService
 * This service is a central access point for configuring the theme, switching between dark and light mode, etc...
 * It uses the theme's LayoutService but also switches our custom mode specific CSS file.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
    private systemThemeWatchRegistered = false;
    public useSystemTheme = false;

    constructor(private layoutService: LayoutService) {}

    public resetThemeMode(): void {
        this.changeColorScheme('system');
    }

    public changeColorScheme(scheme: ThemeMode): void {
        if (scheme === 'system') {
            scheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }

        const isDarkTheme = this.layoutService.isDarkTheme();

        if ((isDarkTheme && scheme === 'dark') || (!isDarkTheme && scheme === 'light')) {
            console.log('Color scheme is already set to ' + scheme);
            return;
        }

        this.switchThemeModeCss(scheme);
        this.switchThemeMode(scheme);
    }

    private switchThemeMode(themeMode: SupportedThemeMode): void {
        // see theme app.configurator.ts:
        this.layoutService.layoutConfig.update((state) => ({
            ...state,
            darkTheme: themeMode === 'dark',
        }));
    }

    private switchThemeModeCss(themeMode: SupportedThemeMode): void {
        const cssFile = themeMode === 'light' ? 'assets/theme-modes/light.css' : 'assets/theme-modes/dark.css';

        const oldThemeStyleEl = document.getElementById('theme-mode') as HTMLLinkElement | null;
        if (oldThemeStyleEl) {
            oldThemeStyleEl.parentNode?.removeChild(oldThemeStyleEl);
        }

        const newThemeStyleEl = document.createElement('link');
        newThemeStyleEl.id = 'theme-mode';
        newThemeStyleEl.rel = 'stylesheet';
        newThemeStyleEl.href = cssFile;
        document.head.appendChild(newThemeStyleEl);
    }

    private updateThemeColorMeta(): void {
        const styles = getComputedStyle(document.documentElement);

        const light = styles.getPropertyValue('--main-background-color-light').trim();
        const dark = styles.getPropertyValue('--main-background-color-dark').trim();

        const lightMeta = document.getElementById('theme-color-light') as HTMLMetaElement | null;
        const darkMeta = document.getElementById('theme-color-dark') as HTMLMetaElement | null;

        if (lightMeta && light) {
            lightMeta.content = light;
        }
        if (darkMeta && dark) {
            darkMeta.content = dark;
        }
    }

    /**
     * This method configures the theme with our custom configuration.
     * Called on app init.
     */
    public configureTheme(): void {
        this.layoutService.layoutConfig.update((state) => ({
            ...state,
            cardStyle: 'filled',
        }));
        this.resetThemeMode();
        this.updateThemeColorMeta();
        this.watchSystemThemeChanges();
    }

    private watchSystemThemeChanges(): void {
        if (this.systemThemeWatchRegistered) {
            return;
        }
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
            if (this.useSystemTheme) {
                this.changeColorScheme(e.matches ? 'dark' : 'light');
            }
        });
        this.systemThemeWatchRegistered = true;
    }

    /**
     * Returns color definitions to be used in JS (not CSS), e.g. for charts.
     * @returns An object of color definitions.
     */
    public colors(): JsThemeColors {
        if (!this.layoutService.isDarkTheme()) {
            // light mode:
            return {
                chartLabelColor: '#333333',
                chartBorderColor: '#ffffff',
                chartAxisLabelColor: '#4b5563', // gray-600: #4b5563
            };
        } else {
            // dark mode:
            return {
                chartLabelColor: '#ffffff',
                chartBorderColor: '#18181b',
                chartAxisLabelColor: '#9ca3af', // gray-400: #9ca3af
            };
        }
    }
}

export type JsThemeColors = {
    chartLabelColor: string;
    chartBorderColor: string;
    chartAxisLabelColor: string;
};
